import {
  Button,
  Checkbox,
  CheckboxOnChangeData,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Label,
  makeStyles,
  Textarea,
  TextareaOnChangeData,
} from "@fluentui/react-components";
import { ChangeEvent, useState } from "react";
import { usePublishActivityDraft } from "../../hooks/useActivities";
import { useTranslation } from "react-i18next";
import { useSaveAndPublishActivityDraft } from "../../hooks/useActivities";
import {
  SaveAndPublishActivityRequest,
  PublishActivityRequest,
  UpdateActivityRequest,
} from "../../models/Activity";
import { ClientRoutes } from "../../models/ClientRoutes";
import { useNavigate } from "react-router-dom";
import { ButtonWithLoading } from "../ButtonWithLoading";

const useStyles = makeStyles({
  dialogSurface: {
    width: "400px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
  },
  commentTextArea: {
    minHeight: "150px",
  },
  commentLabel: {
    marginTop: "20px",
  },
});

interface ActivityPublishDialogProps {
  versionId: string;
  activityId?: string;
  mirroredIdsToClear: string[] | undefined;
  disabled?: boolean;
  onPublishStart?: () => void;
  onPublishFailed?: () => void;
  /** The current form data to save, in the same request that publishes the draft. */
  pendingUpdate: Omit<UpdateActivityRequest, "versionId">;
  /** Increment this value to open the dialog programmatically, e.g. after a combined save-and-publish action. */
  openSignal?: number;
  publishOnly?: boolean;
}

export const ActivityPublishDialog = (props: ActivityPublishDialogProps) => {
  const {
    versionId,
    disabled,
    onPublishFailed,
    onPublishStart,
    pendingUpdate,
    openSignal,
    publishOnly,
  } = props;
  const {
    mutateAsync: saveAndPublishActivity,
    isPending: isSaveAndPublishing,
  } = useSaveAndPublishActivityDraft();

  const { mutateAsync: publishActivity, isPending: isPublishing } =
    usePublishActivityDraft();

  const [comment, setComment] = useState<string | undefined>(undefined);
  const [hiddenRevision, setHiddenRevision] = useState<boolean>(false);

  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  // Open the dialog whenever the parent requests it via `openSignal`,
  // e.g. after a combined save-and-publish action.
  const [lastOpenSignal, setLastOpenSignal] = useState(openSignal);
  if (openSignal !== lastOpenSignal) {
    setLastOpenSignal(openSignal);
    if (openSignal) {
      setOpen(true);
    }
  }

  const { t } = useTranslation();

  const onTextAreaChange = (
    _ev: ChangeEvent<HTMLTextAreaElement>,
    data: TextareaOnChangeData
  ) => {
    setComment(data.value);
  };

  const onHiddenRevisionChange = (
    _ev: ChangeEvent<HTMLInputElement>,
    data: CheckboxOnChangeData
  ) => {
    setHiddenRevision(data.checked as boolean);
  };

  const onClickPublish = async () => {
    if (!comment) {
      return;
    }

    if (publishOnly) {
      const request: PublishActivityRequest = {
        versionId: versionId,
        comment: comment,
        hiddenComment: hiddenRevision,
      };

      onPublishStart?.();

      try {
        await publishActivity(request);
        setOpen(false);
        navigate(ClientRoutes.ActivitiesPublished);
      } catch (error) {
        onPublishFailed?.();
        throw error;
      }
    } else {
      const request: SaveAndPublishActivityRequest = {
        versionId: versionId,
        ...pendingUpdate,
        comment: comment,
        hiddenComment: hiddenRevision,
      };

      onPublishStart?.();

      try {
        await saveAndPublishActivity(request);
        setOpen(false);
        navigate(ClientRoutes.ActivitiesPublished);
      } catch (error) {
        onPublishFailed?.();
        throw error;
      }
    }
  };

  return (
    <Dialog open={open}>
      <DialogTrigger disableButtonEnhancement>
        <Button
          size="large"
          appearance="primary"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {t("publish")}
        </Button>
      </DialogTrigger>
      <DialogSurface
        className={classes.dialogSurface}
        aria-describedby={undefined}
      >
        <DialogBody>
          <DialogTitle>{t("publishDialogTitle")}</DialogTitle>
          <DialogContent>
            <div className={classes.content}>
              <div>{t("publishingDescription")}</div>
              <Label className={classes.commentLabel}>
                {t("commentLabel")}
              </Label>
              <Textarea
                value={comment ? comment : ""}
                onChange={onTextAreaChange}
                resize="vertical"
                textarea={{ className: classes.commentTextArea }}
              />
              <Checkbox
                label={t("hideRevisionCheckboxLabel")}
                checked={hiddenRevision}
                onChange={onHiddenRevisionChange}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button onClick={() => setOpen(false)} appearance="secondary">
                {t("close")}
              </Button>
            </DialogTrigger>
            <DialogTrigger disableButtonEnhancement>
              <ButtonWithLoading
                onClick={onClickPublish}
                isLoading={isSaveAndPublishing || isPublishing}
                disabled={!comment}
                text={t("publish")}
              />
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
